import { setCurrentEventContext } from "@features/access/eventContextService";
import logger from "@logger";
import QUERY_KEYS from "@shared/queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

type UseApplyFirstEventDeepLinkOptions = {
  needsFirstApply: boolean;
  validEventId: string | null;
  onApplyError: (message: string) => void;
  onApplyingChange: (isApplying: boolean) => void;
};

export function useApplyFirstEventDeepLink({
  needsFirstApply,
  validEventId,
  onApplyError,
  onApplyingChange,
}: UseApplyFirstEventDeepLinkOptions) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const applyStartedRef = useRef(false);

  const runApply = useCallback(async () => {
    if (!needsFirstApply || !validEventId || applyStartedRef.current) {
      return;
    }

    applyStartedRef.current = true;
    onApplyingChange(true);
    onApplyError("");

    try {
      await setCurrentEventContext(validEventId);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CURRENT_USER] });
      navigate("/discover", { replace: true });
    } catch (error) {
      logger.error("Failed to apply first event context from deep link:", error);
      const message = error instanceof Error ? error.message : "Could not join this event. Try again.";
      onApplyError(message);
      onApplyingChange(false);
    }
  }, [needsFirstApply, validEventId, navigate, onApplyError, onApplyingChange, queryClient]);

  useEffect(() => {
    void runApply();
  }, [runApply]);

  const retryApply = useCallback(() => {
    applyStartedRef.current = false;
    onApplyingChange(false);
    void runApply();
  }, [onApplyingChange, runApply]);

  return { retryApply };
}
